import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FiPlus, FiSend, FiFile, FiX, FiFileText, FiCheck, FiClock, FiPaperclip,
} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';

import { useInsuredDetail } from '../../context/InsuredDetailContext';

const IMPORT_API_URL = `${process.env.REACT_APP_MyAMP_Url || ''}/api/ams/rate-quoting-import`;

// Convert $$IDF_Name##id$$ tokens to markdown links before rendering
function processEntityLinks(text: string): string {
  return text
    .replace(/\$\$IDF_([^#$]+)##([^$]+)\$\$/g, (_, name, id) => `[${name.trim()}](#INSURED_ID_${id.trim()})`)
    .replace(/\$\$PDF_([^#$]+)##([^$]+)\$\$/g, (_, name, id) => `[${name.trim()}](#POLICY_ID_${id.trim()})`);
}

// Strip all remaining backend sentinel markers (IDF/PDF already converted above)
const SENTINEL_RE = /\$\$[A-Z]+START\$\$[\s\S]*?\$\$[A-Z]+END\$\$|\$\$[A-Z]+START\$\$[\s\S]*/g;
function stripSentinels(text: string): string {
  return processEntityLinks(text).replace(SENTINEL_RE, '').trim();
}

type ImportMode = 'insert' | 'quote';

interface ChatMsg {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  files?: { fileName: string; fileURL: string }[];
  toolStatus?: 'active' | 'done';
  toolName?: string;
}

interface ThinkingStep {
  tool: string;
  text: string;
  status: 'active' | 'done';
  input?: string;
  output?: string;
}

interface Props {
  onQuoteStarted: (runId: string) => void;
  onInsertedProspect: () => void;
  lob?: 'personal' | 'commercial';
}

// Quote carrier progress sub-steps
const QUOTE_STEPS = [
  'Connecting to carriers',
  'Fetching available rates',
  'Comparing coverage options',
  'Compiling quote results',
];

// Friendly labels for tool names (supports both LangChain shared_tools and NowCerts MCP tool names)
const TOOL_LABELS: Record<string, string> = {
  // Document scanning (LangChain legacy)
  policy_summarize_tool_using_file: 'Scanning document',
  insert_summarized_insured_policy_details_in_ams: 'Inserting into AMS',
  // NowCerts MCP tools (Account Manager Agent)
  insert_insured_prospect_tool: 'Creating prospect',
  bulk_insert_insured_prospect_tool: 'Creating prospect',
  insert_vehicle_tool: 'Adding vehicles',
  bulk_insert_vehicles: 'Adding vehicles',
  insert_driver_tool: 'Adding drivers',
  insert_property_location: 'Adding property location',
  bulk_insert_policy_in_ams: 'Creating policy',
  insert_policy_in_ams: 'Creating policy',
  bulk_insert_opportunities: 'Creating opportunity',
  insert_opportunity: 'Creating opportunity',
  insert_policy_or_quote_coverages: 'Adding coverages',
  trigger_rate_quote: 'Starting rate quoting',
  // MCP insert tools (alternative names)
  bulk_insert_vehicle_tool: 'Adding vehicles',
  insert_quote_tool: 'Creating quote record',
  insert_quote_in_ams: 'Creating quote record',
  insert_policy_coverages_tool: 'Attaching coverages',
  insert_opportunity_tool: 'Creating opportunity',
  // Lookup tools
  get_insured_id: 'Looking up insured',
  get_insured_list: 'Searching insureds',
  get_insured_details_tool: 'Looking up insured details',
  get_vehicle_list: 'Fetching vehicles',
  get_vehicle_list_tool: 'Fetching vehicles',
  get_driver_list: 'Fetching drivers',
  get_policy_quote_list: 'Fetching policy details',
  get_policy_list_tool: 'Fetching policies',
  get_line_of_business_list_tool: 'Loading business lines',
  get_opportunity_stage_name_tool: 'Loading opportunity stages',
  get_appetite: 'Checking carrier appetite',
};

export const RateQuoteLinkImportTab: React.FC<Props> = ({ onQuoteStarted, onInsertedProspect, lob = 'personal' }) => {
  const { setSelectedInsuredId } = useInsuredDetail();

  // Custom markdown renderer — intercepts #INSURED_ID_ links to open right sidebar
  const markdownComponents = useMemo(() => ({
    a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }) => {
      if (href?.startsWith('#INSURED_ID_')) {
        const insuredId = href.replace('#INSURED_ID_', '');
        return (
          <button
            className="rql-entity-chip rql-entity-chip--insured"
            onClick={() => setSelectedInsuredId(insuredId)}
            type="button"
          >
            {children}
          </button>
        );
      }
      if (href?.startsWith('#POLICY_ID_')) {
        return (
          <button
            className="rql-entity-chip rql-entity-chip--policy"
            type="button"
          >
            {children}
          </button>
        );
      }
      return <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>;
    },
  }), [setSelectedInsuredId]);

  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [conversationId, setConversationId] = useState<string>(() => crypto.randomUUID());
  const [isDragging, setIsDragging] = useState(false);
  const [selectedMode, setSelectedMode] = useState<ImportMode | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  // Track detected insured from last import — used for quote trigger on follow-up
  const lastInsuredRef = useRef<{ id: string; name: string; policyType: string; state: string }>({ id: '', name: '', policyType: '', state: '' });
  const [streamingText, setStreamingText] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [quoteProgressStep, setQuoteProgressStep] = useState(-1);
  const quoteProgressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [chatHistory, setChatHistory] = useState<{ conversationId: string; preview: string; fileName: string; date: string }[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const centerFileRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll only when user is near the bottom — prevents "dancing" while scrolling up
  const isNearBottomRef = useRef(true);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !isNearBottomRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, thinkingSteps, streamingText]);

  // Start animated quote progress when trigger_rate_quote fires
  const startQuoteProgress = useCallback(() => {
    setQuoteProgressStep(0);
    let step = 0;
    quoteProgressRef.current = setInterval(() => {
      step += 1;
      if (step >= QUOTE_STEPS.length - 1) {
        clearInterval(quoteProgressRef.current!);
        quoteProgressRef.current = null;
      }
      setQuoteProgressStep(step);
    }, 1800);
  }, []);

  const stopQuoteProgress = useCallback(() => {
    if (quoteProgressRef.current) {
      clearInterval(quoteProgressRef.current);
      quoteProgressRef.current = null;
    }
    setQuoteProgressStep(-1);
  }, []);

  // User-facing display message (short, friendly)
  const DISPLAY_PROMPTS: Record<ImportMode, string> = {
    insert: 'Scan this document, extract all details, and insert into NowCerts AMS. Show me the summary before quoting.',
    quote: 'Scan this document, insert everything into AMS, and start quoting.',
  };

  // Full technical prompt sent to the agent (hidden from user)
  const buildPrompt = useCallback((mode: ImportMode, text?: string): string => {
    if (text) return text;

    const RULES = `CRITICAL RULES:
- Do NOT narrate intermediate steps. Work silently — deliver ONE complete response at the end.
- ALWAYS prefer MCP tools over custom tools. If a custom tool fails, do NOT retry it — switch to the MCP version immediately.
- NEVER retry a tool call with the same parameters. Every retry must change something.
- If a required field is missing from the document, pass empty string "" — do NOT skip the tool call.
- NEVER skip any step. Insert everything available and flag what's missing.`;

    if (lob === 'commercial') {
      const base = `Read the uploaded document and extract ALL commercial insurance details.

${RULES}

Execute ALL steps IN ORDER using these EXACT MCP tools:

1. **Prospect**: Call MCP insert_insured_prospect_tool (type: 1 Prospect, insuredType: "2" Commercial). Include: commercialName (business name), addressLine1, city, state, zipCode, eMail, phone, cellPhone. Use the business/company name from the document as commercialName.

2. **CL Rating Data**: Call MCP update_cl_rating_data_tool with: insured_database_id, naic (NAICS code), year_biz_started, number_of_full_time_employees, total_payroll, annual_sales, description (business operations), years_of_operation, sic (SIC code if available). Extract from the dec page — look for employee counts, payroll, revenue/sales, NAICS/SIC codes, business start year.

3. **Quote**: Call MCP insert_quote_tool with: insured_database_id, quote_number, effective_date, expiration_date, premium, line_of_business (use the exact LOB from the document: "Workers Compensation", "General Liability", "Business Owners Policy", "Cyber Liability", etc.). This returns policyOrQuoteId.

4. **Coverages**: Call MCP insert_policy_coverages_tool with policyDatabaseId (= policyOrQuoteId from step 3) and coverage details from the document.

5. **Opportunity**: Call MCP insert_opportunity_tool with: insured_database_id, line_of_business_name, needed_by (YYYY-MM-DD), opportunity_stage_name ("New Lead"), win_probability ("Good"), description, is_policy_renewal (false).

6. **Property/Locations** (BOP/GL with locations): Call insert_property_location with each business location address.`;

      if (mode === 'insert') {
        return base + `

After ALL tool calls complete, show ONE summary response with:
- Prospect created (business name, address)
- CL Rating Data (NAICS, employees, payroll, revenue, business description)
- Quote record created (number, dates, premium, LOB)
- Coverages attached (list of coverage types and limits)
- Locations/Properties (if applicable)
- Opportunity created
- Missing fields that need user input (if any)

MANDATORY — the VERY LAST line of your response MUST be exactly:
"Would you like me to generate competitive quotes for this insured? (WC / GL / BOP / Cyber)"
This question must ALWAYS appear at the end, even if there are missing items. Never end with any other question or offer instead.`;
      }
      return base + `

Insert everything directly without asking for confirmation. After ALL tool calls complete, show ONE summary response with tables for prospect, CL rating data, quote record, coverages, locations, and opportunity.`;
    }

    // Personal lines prompt
    const base = `Read the uploaded document and extract ALL insurance details.

${RULES}

Execute ALL steps IN ORDER using these EXACT MCP tools:

1. **Prospect**: Call the MCP insert_insured_prospect_tool (type: 1 Prospect, insuredType: "1" Personal). Include: firstName, lastName, middleName, addressLine1, city, state, zipCode, eMail, phone, cellPhone, dateOfBirth (YYYY-MM-DD). Add coInsuredFirstName/coInsuredLastName/coInsuredDateOfBirth if co-insured exists.

2. **Vehicles**: Call MCP insert_vehicle_tool for EACH vehicle. Include: insured_database_id, year, make, model, vin, type (e.g. "Sedan", "SUV", "Truck"). Also include policyLevelCoverages array with coverage details from the document:
   policyLevelCoverages: [{"name": "Bodily Injury", "typeCode": "BI", "limits": [100000, 300000], "premium": 0}, {"name": "Property Damage", "typeCode": "PD", "limits": [100000], "premium": 0}, ...]
   Include vehicleSpecificCoverages: [{"name": "Comprehensive", "typeCode": "COMP", "deductibles": [500], "premium": 0}, {"name": "Collision", "typeCode": "COLL", "deductibles": [500], "premium": 0}]

3. **Drivers**: Call MCP insert_driver_tool for EACH driver. ALL these fields are required — pass empty string "" or 0 if not in document: first_name, last_name, date_of_birth (MM/DD/YYYY), license_number, license_state, gender ("Male"/"Female"/""), driver_license_class ("NoCdl" if unknown), termination_date (""), cdl_date (""), cdl_year (0), license_year (0), active (true), insured_database_id.

4. **Quote**: Call MCP insert_quote_tool with: insured_database_id, quote_number, effective_date, expiration_date, premium, line_of_business ("Personal Auto" or "Homeowners"). This returns policyOrQuoteId for coverages.

5. **Coverages**: Call MCP insert_policy_coverages_tool with policyDatabaseId (= policyOrQuoteId from step 4) and the coverage details.

6. **Opportunity**: Call MCP insert_opportunity_tool with: insured_database_id, line_of_business_name, needed_by (YYYY-MM-DD), opportunity_stage_name ("New Lead"), win_probability ("Good"), description, is_policy_renewal (false).

7. **Property** (Homeowners only): Call insert_property_location with property address.`;

    if (mode === 'insert') {
      return base + `

After ALL tool calls complete, show ONE summary response with:
- Prospect created (name, address)
- Vehicles inserted (table: year/make/model/VIN)
- Drivers inserted (table: name/DOB/license)
- Quote record created (number, dates, premium)
- Coverages attached (list of coverage types and limits)
- Property location (for HO)
- Opportunity created
- Missing fields that need user input (if any)

MANDATORY — the VERY LAST line of your response MUST be exactly:
"Would you like me to generate competitive quotes for this insured? (Personal Auto / Homeowners)"
This question must ALWAYS appear at the end, even if there are missing items or lienholders to discuss. Never end with any other question or offer instead.`;
    }
    return base + `

Insert everything directly without asking for confirmation. After ALL tool calls complete, show ONE summary response with tables for prospect, vehicles, drivers, quote record, coverages, and opportunity.`;
  }, [lob]);

  // Upload files via MomentumAMP API -> Azure Blob -> returns blob URL
  const uploadFiles = useCallback(async (files: File[]): Promise<{ fileName: string; fileURL: string }[]> => {
    const results: { fileName: string; fileURL: string }[] = [];
    const authToken = localStorage.getItem('authToken') || '';

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('formFile', file);
        const resp = await fetch(`${(process.env.REACT_APP_AccountAMP_Url || 'https://momentumampapi-stage.insurancegig.com')}/api/MomentumAMP/upload-file/${conversationId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${authToken}` },
          body: formData,
        });
        if (resp.ok) {
          const json = await resp.json();
          results.push({
            fileName: json.fileName || file.name,
            fileURL: json.fileUrl || '',
          });
        }
      } catch {
        results.push({ fileName: file.name, fileURL: '' });
      }
    }
    return results;
  }, [conversationId]);

  // Abort controller for cancelling streams
  const abortRef = useRef<AbortController | null>(null);

  // Send message via unified import endpoint (Claude Managed Agent → AMS → Rate Quoting)
  const sendMessage = useCallback(async (initialText: string, files: File[], initialMode?: ImportMode) => {
    let text = initialText;
    let mode = initialMode;
    // Separate XML files (read as text) from document files (upload to blob)
    const xmlFiles = files.filter(f => f.name.toLowerCase().endsWith('.xml'));
    const nonXmlFiles = files.filter(f => !f.name.toLowerCase().endsWith('.xml'));

    // Read XML files as text via FileReader
    const xmlContents: { fileName: string; xmlText: string }[] = [];
    for (const xf of xmlFiles) {
      const xmlText = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsText(xf);
      });
      xmlContents.push({ fileName: xf.name, xmlText });
    }

    // Upload non-XML files to blob storage
    let filePaths: { fileName: string; fileURL: string }[] = [];
    if (nonXmlFiles.length > 0) {
      setIsUploading(true);
      filePaths = await uploadFiles(nonXmlFiles);
      setIsUploading(false);
    }

    // Auto-detect: if text looks like XML (pasted in chat), treat as XML content
    const trimmedText = text.trim();
    if (!xmlContents.length && trimmedText && (trimmedText.startsWith('<?xml') || trimmedText.includes('<IVANSExtract>'))) {
      xmlContents.push({ fileName: 'pasted-ivans.xml', xmlText: trimmedText });
      text = '';  // clear text so buildPrompt generates full technical instructions
      if (!mode) mode = 'insert';
    }

    // Combine all file info for display badges
    const allFileInfo = [
      ...filePaths,
      ...xmlContents.map(xc => ({ fileName: xc.fileName, fileURL: '' })),
    ];

    const prompt = mode ? buildPrompt(mode, text || undefined) : (text || 'Please scan the uploaded document.');
    // Show short friendly message to user, send full technical prompt to agent
    const displayText = (mode && !text) ? DISPLAY_PROMPTS[mode] || 'Scan IVANS XML and insert into NowCerts AMS.' : (text || prompt);

    const userMsg: ChatMsg = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: displayText,
      files: allFileInfo.length > 0 ? allFileInfo : undefined,
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setPendingFiles([]);
    setSelectedMode(null);
    setIsStreaming(true);
    setThinkingSteps([]);

    // Collect answer text across the stream
    let answerBuf = '';

    const abortCtrl = new AbortController();
    abortRef.current = abortCtrl;

    try {
      const authToken = localStorage.getItem('authToken') || '';
      const ncToken = localStorage.getItem('amsToken') || '';

      // Build form data for the unified import endpoint
      const formData = new FormData();
      formData.append('human_prompt', prompt);
      formData.append('conversationId', conversationId);
      formData.append('nc_token', ncToken);
      formData.append('mode', mode || 'insert');
      formData.append('lob', lob);
      formData.append('file_paths', JSON.stringify(filePaths));
      if (xmlContents.length > 0) {
        formData.append('xml_contents', JSON.stringify(xmlContents));
      }
      // Pass last insured details for quote confirmation follow-ups
      if (lastInsuredRef.current.id) {
        formData.append('insured_id', lastInsuredRef.current.id);
        formData.append('insured_name', lastInsuredRef.current.name);
        formData.append('policy_type', lastInsuredRef.current.policyType);
      }

      const res = await fetch(IMPORT_API_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData,
        signal: abortCtrl.signal,
      });

      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const raw = trimmed.slice(5).trim();
          if (!raw || raw === '[DONE]') continue;

          let evt: Record<string, unknown>;
          try { evt = JSON.parse(raw); } catch { continue; }
          const evtType = (evt.type as string) || '';

          // ── Helper: flush accumulated streaming text as a committed message ──
          const flushStreamingText = () => {
            if (answerBuf) {
              const flushed = stripSentinels(answerBuf);
              if (flushed) {
                setMessages(prev => [...prev, { id: `ai-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, role: 'assistant', content: flushed }]);
              }
              setStreamingText('');
              answerBuf = '';
            }
          };

          // ── Thought events (tool start/complete) ─────────────────
          if (evtType === 'thought') {
            const toolName = (evt.currentTool as string) || '';
            if (toolName) {
              const cleanName = toolName.replace(/^mcp_/, '');
              const label = TOOL_LABELS[cleanName] || TOOL_LABELS[toolName] || cleanName.replace(/_/g, ' ');
              const toolMsgId = `tool-${toolName}`;

              if (evt.toolComplete) {
                // Mark tool as done in messages
                setMessages(prev => prev.map(m =>
                  m.id === toolMsgId ? { ...m, toolStatus: 'done' as const } : m
                ));
              } else {
                // New tool starting — flush any accumulated streaming text first
                flushStreamingText();
                // Mark all previous running tools as done, add new tool message
                setMessages(prev => {
                  if (prev.find(m => m.id === toolMsgId)) return prev;
                  const updated = prev.map(m =>
                    m.role === 'tool' && m.toolStatus === 'active' ? { ...m, toolStatus: 'done' as const } : m
                  );
                  return [...updated, { id: toolMsgId, role: 'tool' as const, content: label, toolStatus: 'active' as const, toolName: cleanName }];
                });
              }
            } else if (!answerBuf) {
              // Initial thought — show analyzing step
              setMessages(prev => {
                if (prev.find(m => m.id === 'tool-_analyzing')) return prev;
                return [...prev, { id: 'tool-_analyzing', role: 'tool' as const, content: 'Analyzing document', toolStatus: 'active' as const, toolName: '_analyzing' }];
              });
            }
            continue;
          }

          // ── Answer streaming ─────────────────────────────────────
          if (evtType === 'answer') {
            const response = (evt.response as string) || '';
            const streaming = evt.responseStreaming !== false;

            if (streaming && response) {
              // Live stream — show text as it arrives
              answerBuf += response;
              setStreamingText(stripSentinels(answerBuf));
            }

            if (!streaming) {
              // Terminal answer event — flush any remaining streaming text
              flushStreamingText();
              // Also handle the terminal response content if different from buffer
              const terminalContent = stripSentinels(response || '');
              if (terminalContent) {
                setMessages(prev => [...prev, { id: `ai-final-${Date.now()}`, role: 'assistant', content: terminalContent }]);
              }
              // Mark all remaining tool messages as done
              setMessages(prev => prev.map(m =>
                m.role === 'tool' && m.toolStatus === 'active' ? { ...m, toolStatus: 'done' as const } : m
              ));
              setStreamingText('');
              setIsStreaming(false);
              setThinkingSteps([]);
              stopQuoteProgress();
              answerBuf = '';

              const allContent = terminalContent || '';
              if (allContent.includes('✅') || evt.insuredClientId) {
                onInsertedProspect();
                if (evt.insuredClientId && !lastInsuredRef.current.id) {
                  const stMatch = allContent.match(/,\s*([A-Z]{2})\s+\d{5}/);
                  lastInsuredRef.current = {
                    id: evt.insuredClientId as string,
                    name: '',
                    policyType: /homeowners|HO-3|dwelling/i.test(allContent) ? 'HO' : 'PAP',
                    state: stMatch ? stMatch[1] : '',
                  };
                }
              }
              if (evt.runId) {
                setIsRedirecting(true);
                onQuoteStarted(evt.runId as string);
              }
            }
            continue;
          }

          // ── Import complete (backend detected insured + optional rate quoting) ──
          if (evtType === 'import_complete') {
            // Save insured details for quote confirmation follow-ups
            let insuredName = (evt.insuredName as string) || lastInsuredRef.current.name || '';
            // Try to extract name from accumulated response if still empty
            if (!insuredName && answerBuf) {
              const nm = answerBuf.match(/(?:Prospect|Insured)\s*(?:created|:)[:\s—]*\*?\*?([A-Z][a-zA-Z.'\- ]{3,40})/i);
              if (nm) insuredName = nm[1].replace(/\*+/g, '').trim();
            }
            if (evt.insuredId) {
              lastInsuredRef.current = {
                id: evt.insuredId as string,
                name: insuredName,
                policyType: (evt.policyType as string) || 'PAP',
                state: (evt.state as string) || '',
              };
              onInsertedProspect();
            }
            if (evt.runId) {
              // Show a brief message before redirecting
              const pType = (evt.policyType as string) === 'HO' ? 'Homeowners' : 'Personal Auto';
              setMessages(prev => [...prev, {
                id: `ai-quote-${Date.now()}`,
                role: 'assistant',
                content: `Starting **${pType}** rate quoting for **${insuredName || 'the insured'}**. Opening workflow panel...`,
              }]);
              setIsStreaming(false);
              setThinkingSteps([]);
              setIsRedirecting(true);
              setTimeout(() => onQuoteStarted(evt.runId as string), 800);
            }
            continue;
          }

          // ── Error ────────────────────────────────────────────────
          if (evtType === 'error') {
            setStreamingText('');
            setMessages(prev => [...prev, {
              id: `err-${Date.now()}`,
              role: 'assistant',
              content: (evt.error as string) || 'Something went wrong. Please try again.',
            }]);
            setIsStreaming(false);
            setThinkingSteps([]);
            stopQuoteProgress();
            continue;
          }
        }
      }

      // Stream ended without terminal event — finalize what we have
      if (answerBuf && isStreaming) {
        const finalContent = stripSentinels(answerBuf);
        setStreamingText('');
        if (finalContent) {
          setMessages(prev => [...prev, { id: `ai-${Date.now()}`, role: 'assistant', content: finalContent }]);
        }
        setIsStreaming(false);
        setThinkingSteps([]);
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      setStreamingText('');
      setMessages(prev => [...prev, { id: `err-${Date.now()}`, role: 'assistant', content: 'Something went wrong. Please try again.' }]);
      setIsStreaming(false);
      setThinkingSteps([]);
      stopQuoteProgress();
    }
  }, [conversationId, uploadFiles, buildPrompt, onInsertedProspect, onQuoteStarted, stopQuoteProgress, isStreaming]);

  // Trigger rate quoting directly via backend (no agent call)
  const triggerQuote = useCallback(async (policyType: string) => {
    const insured = lastInsuredRef.current;
    // Fallback: scan ALL assistant messages for insured ID, name, state
    const assistantMsgs = messages.filter(m => m.role === 'assistant');
    const allText = assistantMsgs.map(m => m.content).join('\n');

    if (!insured.id) {
      // Try UUID from any format: ##uuid, ID: uuid, (uuid), "uuid"
      const idMatch = allText.match(/([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i);
      if (idMatch) insured.id = idMatch[1];
    }
    if (!insured.name) {
      // Try markdown link [Name](#INSURED_ID_...)
      const linkMatch = allText.match(/\[([^\]]+)\]\(#INSURED_ID/);
      if (linkMatch) { insured.name = linkMatch[1].trim(); }
      // Try "Prospect Created" or "Prospect:" patterns
      else {
        const namePatterns = [
          /Prospect\s*[Cc]reated[:\s—]*\*?\*?([A-Z][a-zA-Z.'\- ]+)/,
          /(?:Named Insured|Insured)[:\s—]+\*?\*?([A-Z][a-zA-Z.'\- ]{3,40})/,
          /(?:1\.|✅)\s*(?:Prospect|Insured)[:\s—]+\*?\*?([A-Z][a-zA-Z.'\- ]{3,40})/,
        ];
        for (const pattern of namePatterns) {
          const m = allText.match(pattern);
          if (m) { insured.name = m[1].replace(/\*+/g, '').trim(); break; }
        }
      }
      // Last resort: extract from file name (e.g., "01_Personal_Auto_Dec_Whitfield_Austin.pdf")
      if (!insured.name) {
        const userMsg = messages.find(m => m.role === 'user' && m.files?.length);
        if (userMsg?.files?.[0]?.fileName) {
          const parts = userMsg.files[0].fileName.replace(/\.pdf$/i, '').split(/[_\-]/);
          // Find a part that looks like a last name (capitalized, 3+ chars, not a keyword)
          const skip = new Set(['personal', 'auto', 'dec', 'pap', 'ho', 'home', 'ok', 'tx', 'il', 'ma', 'md']);
          const nameParts = parts.filter(p => p.length >= 3 && /^[A-Z]/.test(p) && !skip.has(p.toLowerCase()));
          if (nameParts.length >= 1) insured.name = nameParts.join(' ');
        }
      }
    }
    if (!insured.state) {
      const stateMatch = allText.match(/,\s*([A-Z]{2})\s+\d{5}/);
      if (stateMatch) insured.state = stateMatch[1];
    }
    insured.policyType = policyType;
    lastInsuredRef.current = { ...insured };
    if (!insured.id) {
      setMessages(prev => [...prev, { id: `err-${Date.now()}`, role: 'assistant', content: 'No insured found from import. Please import a document first.' }]);
      return;
    }

    setIsStreaming(true);
    setMessages(prev => [...prev, {
      id: `user-${Date.now()}`,
      role: 'user',
      content: `Generate ${policyType === 'HO' ? 'Homeowners' : 'Personal Auto'} quotes`,
    }]);

    try {
      const authToken = localStorage.getItem('authToken') || '';
      const ncToken = localStorage.getItem('amsToken') || '';

      const formData = new FormData();
      formData.append('human_prompt', `yes run ${policyType} quote`);
      formData.append('conversationId', conversationId);
      formData.append('nc_token', ncToken);
      formData.append('mode', 'quote');
      formData.append('lob', lob);
      formData.append('file_paths', '[]');
      formData.append('insured_id', insured.id);
      formData.append('insured_name', insured.name);
      formData.append('policy_type', policyType);
      if (insured.state) formData.append('state_code', insured.state);

      const res = await fetch(IMPORT_API_URL, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: formData,
      });

      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let redirected = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const raw = trimmed.slice(5).trim();
          if (!raw) continue;
          try {
            const evt = JSON.parse(raw);
            // Detect runId from any event and redirect immediately
            const runId = evt.runId || (evt.type === 'import_complete' && evt.runId);
            if (runId && !redirected) {
              redirected = true;
              setIsRedirecting(true);
              setMessages(prev => [...prev, {
                id: `ai-${Date.now()}`,
                role: 'assistant',
                content: `Rate quoting started. Opening workflow panel...`,
              }]);
              // Small delay to show the message before redirect
              setTimeout(() => onQuoteStarted(runId), 500);
            }
            if (evt.type === 'answer' && evt.responseStreaming === false && evt.response && !redirected) {
              setMessages(prev => [...prev, { id: `ai-${Date.now()}`, role: 'assistant', content: stripSentinels(evt.response || '') }]);
            }
          } catch { /* skip */ }
        }
      }
    } catch {
      setMessages(prev => [...prev, { id: `err-${Date.now()}`, role: 'assistant', content: 'Failed to start quoting. Please try from the Prospects tab.' }]);
    }
    setIsStreaming(false);
  }, [conversationId, onQuoteStarted, messages]);

  // Handle send from input bar
  const handleSend = useCallback(() => {
    const text = input.trim();
    const files = [...pendingFiles];
    if (!text && files.length === 0) return;
    sendMessage(text, files, files.length > 0 && !text ? selectedMode || 'insert' : undefined);
  }, [input, pendingFiles, selectedMode, sendMessage]);

  // Handle center action button clicks
  const handleCenterAction = useCallback((mode: ImportMode) => {
    setSelectedMode(mode);
    centerFileRef.current?.click();
  }, []);

  const handleCenterFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length > 0) sendMessage('', files, selectedMode || 'insert');
  }, [selectedMode, sendMessage]);

  // Drag & drop
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) sendMessage('', files, 'insert');
  }, [sendMessage]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }, [handleSend]);

  // Load history from API
  const loadHistory = useCallback(async () => {
    setShowHistory(true);
    setHistoryLoading(true);
    try {
      const authToken = localStorage.getItem('authToken') || '';
      const resp = await fetch(`${(process.env.REACT_APP_MyAMP_Url)}/api/ams/rate-quoting-import-chat/history`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (resp.ok) {
        const data = await resp.json();
        setChatHistory(data.conversations || []);
      }
    } catch { /* ignore */ }
    setHistoryLoading(false);
  }, []);

  // Load a conversation from history into the chat area
  const loadConversation = useCallback(async (convId: string) => {
    setShowHistory(false);
    setIsStreaming(false);
    setThinkingSteps([]);
    // Switch to the loaded conversation so follow-up messages stay in the same thread
    setConversationId(convId);
    try {
      const authToken = localStorage.getItem('authToken') || '';
      const resp = await fetch(`${process.env.REACT_APP_MyAMP_Url}/api/ams/rate-quoting-import-chat/history/${encodeURIComponent(convId)}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!resp.ok) return;
      const data = await resp.json();
      const histMsgs: { id: string; role: string; content: string }[] = data.messages || [];
      if (histMsgs.length > 0) {
        setMessages(histMsgs
          .filter(m => m.role === 'user' || m.role === 'assistant')
          .map(m => ({
            id: m.id,
            role: m.role as 'user' | 'assistant',
            content: m.content,
          })));
      }
    } catch { /* ignore */ }
  }, []);

  const isEmpty = messages.length === 0;

  return (
    <div
      className="rql-import-chat"
      onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="rql-import-chat__drop-overlay">
          <FiFileText size={32} />
          <span>Drop document here to scan</span>
        </div>
      )}

      {/* Hidden file input for center actions */}
      <input ref={centerFileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx,.xml" style={{ display: 'none' }} onChange={handleCenterFileSelected} />

      {/* Messages */}
      <div className="rql-import-chat__messages" ref={scrollRef} onScroll={handleScroll}>
        {isEmpty ? (
          <div className="rql-import-chat__empty">
            <div className="rql-import-chat__empty-top">
              <div className="rql-import-chat__empty-icon"><RiSparklingFill size={24} /></div>
              <h3>Import & Quote</h3>
              <p>Upload a policy document and let AI handle the rest — extract insured details, drivers, vehicles, coverages, and properties. Insert everything into NowCerts AMS and generate competitive quotes from multiple carriers.</p>
            </div>

            {/* 2 action cards */}
            <div className="rql-import-chat__actions rql-import-chat__actions--two">

              {/* Card 1 — Scan & Insert */}
              <button className="rql-import-chat__action-btn rql-import-chat__action-btn--insert" onClick={() => handleCenterAction('insert')}>
                <div className="rql-import-chat__action-head">
                  <span className="rql-import-chat__action-icon-wrap">⚡</span>
                  <div className="rql-import-chat__action-titles">
                    <span className="rql-import-chat__action-title">Upload, Scan & Insert</span>
                    <span className="rql-import-chat__action-desc">Upload a document or IVANS XML, scan &amp; extract details, then review before quoting.</span>
                  </div>
                </div>
                <ul className="rql-import-chat__action-steps-list">
                  <li><span className="rql-ais-dot rql-ais-dot--1" />Scan &amp; extract all details</li>
                  <li><span className="rql-ais-dot rql-ais-dot--2" />Create prospect in NowCerts</li>
                  <li><span className="rql-ais-dot rql-ais-dot--3" />Add vehicles &amp; drivers</li>
                  <li><span className="rql-ais-dot rql-ais-dot--confirm" />Confirm before quoting</li>
                </ul>
                <div className="rql-import-chat__action-upload-cta">
                  <FiPaperclip size={10} />
                  Click to upload
                </div>
              </button>

              {/* Card 2 — Upload, Scan, Insert & Quote (recommended) */}
              <button className="rql-import-chat__action-btn rql-import-chat__action-btn--quote" onClick={() => handleCenterAction('quote')}>
                <div className="rql-import-chat__action-head">
                  <span className="rql-import-chat__action-icon-wrap rql-import-chat__action-icon-wrap--glow">✨</span>
                  <div className="rql-import-chat__action-titles">
                    <div className="rql-import-chat__action-quote-top">
                      <span className="rql-import-chat__action-title">Upload, Scan, Insert &amp; Quote</span>
                      <span className="rql-import-chat__action-tag rql-import-chat__action-tag--primary">Recommended</span>
                    </div>
                    <span className="rql-import-chat__action-desc">Upload a document or IVANS XML, auto-insert into AMS and get competitive rates — no confirmation needed.</span>
                  </div>
                </div>
                <ul className="rql-import-chat__action-steps-list">
                  <li><span className="rql-ais-dot rql-ais-dot--1" />Upload &amp; scan document</li>
                  <li><span className="rql-ais-dot rql-ais-dot--2" />Insert prospect, vehicles &amp; drivers</li>
                  <li><span className="rql-ais-dot rql-ais-dot--3" />Create policy &amp; coverages</li>
                  <li><span className="rql-ais-dot rql-ais-dot--confirm" />Quote across multiple carriers</li>
                </ul>
                <div className="rql-import-chat__action-upload-cta">
                  <FiPaperclip size={10} />
                  Click to upload
                </div>
              </button>

            </div>

            <div className="rql-import-chat__empty-footer">
              <span className="rql-import-chat__empty-hint">Drag & drop a file anywhere on this page</span>
              <div className="rql-import-chat__empty-chips">
                <span>PDF</span>
                <span>ACORD Forms</span>
                <span>Dec Pages</span>
                <span>XML / IVANS</span>
                <span>PNG / JPG</span>
              </div>
            </div>
          </div>
        ) : (
          messages.filter(m => m.role !== 'tool' || m.role === 'tool').map(msg => {
            // ── Tool card (inline in chat flow) ──
            if (msg.role === 'tool') {
              return (
                <div key={msg.id} className="rql-import-chat__msg rql-import-chat__msg--tool">
                  <div className={`rql-import-tool-card${msg.toolStatus === 'done' ? ' rql-import-tool-card--done' : ''}`}>
                    <div className="rql-import-tool-card__header">
                      {msg.toolStatus === 'active'
                        ? <RiSparklingFill size={12} className="rql-import-tool-card__spin" />
                        : <FiCheck size={12} className="rql-import-tool-card__check" />}
                      <span className="rql-import-tool-card__name">{msg.content}</span>
                      {msg.toolStatus === 'active'
                        ? <span className="rql-import-tool-card__status rql-import-tool-card__status--running">Running</span>
                        : <span className="rql-import-tool-card__status rql-import-tool-card__status--done">Done</span>}
                    </div>
                  </div>
                </div>
              );
            }
            // ── User message ──
            if (msg.role === 'user') {
              return (
                <div key={msg.id} className="rql-import-chat__msg rql-import-chat__msg--user">
                  <div className="rql-import-chat__user-msg">
                    {msg.files && msg.files.length > 0 && (
                      <div className="rql-import-chat__files">
                        {msg.files.map((f, i) => (
                          <span key={i} className="rql-import-chat__file-badge"><FiFile size={11} /> {f.fileName}</span>
                        ))}
                      </div>
                    )}
                    <p className="rql-import-chat__user-text">{msg.content}</p>
                  </div>
                </div>
              );
            }
            // ── Assistant message ──
            return (
              <div key={msg.id} className="rql-import-chat__msg rql-import-chat__msg--assistant">
                <div className="rql-import-chat__avatar"><RiSparklingFill size={14} /></div>
                <div className="rql-import-chat__bubble">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>{msg.content}</ReactMarkdown>
                </div>
              </div>
            );
          })
        )}

        {/* Quote action buttons — shown after import completes, hidden after quote triggered */}
        {!isStreaming && !isRedirecting && lastInsuredRef.current.id && messages.length > 0 && !messages.some(m => m.content.includes('Rate quoting has been started')) && (
          <div className="rql-import-chat__quote-actions">
            <span className="rql-import-chat__quote-actions-label">Generate competitive quotes:</span>
            {lob === 'commercial' ? (
              <>
                <button className="rql-import-chat__quote-btn" onClick={() => triggerQuote('WC')} type="button">
                  🏗️ Workers Comp (WC)
                </button>
                <button className="rql-import-chat__quote-btn" onClick={() => triggerQuote('GL')} type="button">
                  📋 General Liability (GL)
                </button>
                <button className="rql-import-chat__quote-btn" onClick={() => triggerQuote('BOP')} type="button">
                  🏢 Business Owners (BOP)
                </button>
                <button className="rql-import-chat__quote-btn" onClick={() => triggerQuote('Cyber')} type="button">
                  🔒 Cyber Policy
                </button>
              </>
            ) : (
              <>
                <button className="rql-import-chat__quote-btn" onClick={() => triggerQuote('PAP')} type="button">
                  🚗 Personal Auto (PAP)
                </button>
                <button className="rql-import-chat__quote-btn" onClick={() => triggerQuote('HO')} type="button">
                  🏠 Homeowners (HO)
                </button>
              </>
            )}
          </div>
        )}

        {/* Redirecting to workflow loader */}
        {isRedirecting && (
          <div className="rql-import-chat__redirecting">
            <div className="rql-import-chat__redirecting-dots">
              <span /><span /><span />
            </div>
            <span className="rql-import-chat__redirecting-text">Opening quoting workflow…</span>
          </div>
        )}

        {/* Streaming answer — live text as it arrives */}
        {streamingText && (
          <div className="rql-import-chat__msg rql-import-chat__msg--assistant rql-import-chat__msg--stream-answer">
            <div className="rql-import-chat__avatar"><RiSparklingFill size={14} /></div>
            <div className="rql-import-chat__bubble rql-import-chat__bubble--streaming">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingText}</ReactMarkdown>
              <span className="rql-import-chat__cursor" />
            </div>
          </div>
        )}

        {/* Upload loader — centered overlay */}
        {isUploading && (
          <div className="rql-upload-loader">
            <div className="rql-upload-loader__spinner">
              <div className="rql-upload-loader__ring" />
              <div className="rql-upload-loader__icon">
                <FiFileText size={26} />
              </div>
            </div>
            <p className="rql-upload-loader__label">Uploading document</p>
            <p className="rql-upload-loader__sub">Scanning and preparing for AI analysis…</p>
          </div>
        )}

        {/* Initial loading dots before any tool cards or streaming text arrive */}
        {isStreaming && !isUploading && !streamingText && messages.length > 0 && !messages.some(m => m.role === 'tool') && (
          <div className="rql-import-chat__msg rql-import-chat__msg--assistant">
            <div className="rql-import-chat__avatar rql-import-chat__avatar--thinking">
              <RiSparklingFill size={14} />
            </div>
            <div className="rql-import-chat__typing"><span /><span /><span /></div>
          </div>
        )}
      </div>

      {/* Pending files */}
      {pendingFiles.length > 0 && (
        <div className="rql-import-chat__pending-files">
          {pendingFiles.map((f, i) => (
            <span key={i} className="rql-import-chat__pending-file">
              <FiFile size={11} /> {f.name}
              <button onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}><FiX size={10} /></button>
            </span>
          ))}
        </div>
      )}

      {/* History popup */}
      {showHistory && (
        <div className="rql-import-chat__history-overlay" onClick={() => setShowHistory(false)}>
          <div className="rql-import-chat__history-panel" onClick={e => e.stopPropagation()}>
            <div className="rql-import-chat__history-header">
              <span><FiClock size={14} /> Import History</span>
              <button onClick={() => setShowHistory(false)}><FiX size={14} /></button>
            </div>
            <div className="rql-import-chat__history-list">
              {historyLoading ? (
                <div className="rql-import-chat__history-empty">
                  <div className="rql-import-chat__typing" style={{ justifyContent: 'center' }}><span /><span /><span /></div>
                  Loading...
                </div>
              ) : chatHistory.length === 0 ? (
                <div className="rql-import-chat__history-empty">No previous imports yet</div>
              ) : (
                chatHistory.map(h => (
                  <button key={h.conversationId} className="rql-import-chat__history-item" onClick={() => loadConversation(h.conversationId)}>
                    <FiFileText size={13} />
                    <div className="rql-import-chat__history-item-content">
                      <span className="rql-import-chat__history-item-text">{h.fileName || h.preview}</span>
                      <span className="rql-import-chat__history-item-date">{h.date ? new Date(h.date).toLocaleDateString() : ''}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chat input bar */}
      <div className="rql-import-chat__bar">
        {messages.length > 0 && (
          <button type="button" className="rql-import-chat__bar-btn rql-import-chat__bar-btn--new" onClick={() => { if (abortRef.current) abortRef.current.abort(); setMessages([]); setThinkingSteps([]); setIsStreaming(false); setConversationId(crypto.randomUUID()); setStreamingText(''); lastInsuredRef.current = { id: '', name: '', policyType: '', state: '' }; setIsRedirecting(false); }} title="New chat">
            <FiPlus size={13} />
          </button>
        )}
        <button type="button" className="rql-import-chat__bar-btn" onClick={loadHistory} title="Import history">
          <FiClock size={15} />
        </button>
        <button type="button" className="rql-import-chat__bar-btn rql-import-chat__bar-btn--attach" onClick={() => fileInputRef.current?.click()} title="Upload document">
          <FiPaperclip size={15} />
        </button>
        <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.docx,.xml" multiple style={{ display: 'none' }}
          onChange={e => { const files = Array.from(e.target.files || []); setPendingFiles(prev => [...prev, ...files]); e.target.value = ''; }} />
        <div className="rql-import-chat__bar-input-wrap">
          <RiSparklingFill size={13} className="rql-import-chat__bar-sparkle" />
          <textarea ref={inputRef} className="rql-import-chat__bar-input" rows={1} value={input}
            onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
            onPaste={e => {
              const items = Array.from(e.clipboardData?.items || []);
              const imageItems = items.filter(it => it.type.startsWith('image/'));
              if (imageItems.length === 0) return;
              e.preventDefault();
              const files = imageItems.map(it => {
                const f = it.getAsFile();
                if (!f) return null;
                const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
                const ext = f.type.split('/')[1] || 'png';
                return new File([f], `screenshot-${ts}.${ext}`, { type: f.type });
              }).filter(Boolean) as File[];
              if (files.length) setPendingFiles(prev => [...prev, ...files]);
            }}
            placeholder="Upload a document or ask about importing..." disabled={isStreaming} />
        </div>
        <button
          type="button"
          className={`rql-import-chat__bar-send${input.trim() || pendingFiles.length > 0 ? ' rql-import-chat__bar-send--active' : ''}`}
          onClick={handleSend}
          disabled={isStreaming || (!input.trim() && pendingFiles.length === 0)}
          title="Send"
        >
          <FiSend size={14} />
        </button>
      </div>
    </div>
  );
};

